import { createSlice } from '@reduxjs/toolkit';

// Initial state for all tab-specific miscellaneous data
// This replaces all localStorage keys that were previously used
const initialState = {
  // Execute/Runtime data (formerly in Context API)
  executeData: {},
  reportData: {},
  flowSheetSolvdData: {},
  stream_input_dictContext: '',
  ro_ouput_data: '',
  ro_payload: '',
  clro_payload: '',
  'system-units': '',
  unit_set_dict: '',
  default_unit_set_dict: '',

  // Equipment dictionaries
  'stripper-dict': '',
  'mixSplit-dict': '',
  'cf-dict': '',
  'hp-pump-dict': '',
  'pump-dict': '',
  'work_exc-dict': '',
  'pelton_wheel-dict': '',
  'clro-dict': '',
  'turbo_charger-dict': '',
  'press_exc-dict': '',

  // Flow and stream data
  flow_dict: '',
  flow_sheet_dict: '',
  flow_sheet_dict_wastage: '',
  flow_sheet_dict_nfwastage: '',
  product_flow_dict: '',
  feed_flow_dict: '',
  'roHPLoop-stage-data': '',
  streamNum_dict: {},
  localpath: '',

  // Project data
  project_info: '',
  storeNodes: '',
  form: '',

  // Output dictionaries
  'cf-output-dict': '',
  'cd-output-dict': '',
  'edi-output-dict': '',

  // Unit operation naming (dynamic keys for unitop IDs)
  unitOpNames: {},

  // Temp and working data
  'current-unit-work': '',
  'pelton-unit-work': '',
  'turbo-unit-work': '',

  // Active equation data
  Active_DualequationCCPP: {},

  // Tab-specific UI state
  globalExitBtn: false,
};

const miscSlice = createSlice({
  name: 'misc',
  initialState,
  reducers: {
    // Execute/Runtime data (formerly in Context API)
    setExecuteData: (state, action) => { state.executeData = action.payload; },
    updateExecuteData: (state, action) => {
      state.executeData = { ...state.executeData, ...action.payload };
    },
    setMiscKeyValue: (state, action) => {
      const { key, value } = action.payload || {};
      if (!key) return;
      if (value === undefined) {
        delete state[key];
        return;
      }
      state[key] = value;
    },
    setReportData: (state, action) => { state.reportData = action.payload; },
    setFlowSheetSolvdData: (state, action) => { state.flowSheetSolvdData = action.payload; },
    setStreamInputDictContext: (state, action) => { state.stream_input_dictContext = action.payload; },
    setRoOuputData: (state, action) => { state.ro_ouput_data = action.payload; },
    clearExecuteRuntimeData: (state) => {
      state.executeData = {};
      state.reportData = {};
      state.flowSheetSolvdData = {};
      state.stream_input_dictContext = '';
      state.ro_ouput_data = '';
    },

    // Equipment dictionaries
    setStripperDict: (state, action) => { state['stripper-dict'] = action.payload; },
    setMixSplitDict: (state, action) => { state['mixSplit-dict'] = action.payload; },
    setCfDict: (state, action) => { state['cf-dict'] = action.payload; },
    setHpPumpDict: (state, action) => { state['hp-pump-dict'] = action.payload; },
    setPumpDict: (state, action) => { state['pump-dict'] = action.payload; },
    setWorkExcDict: (state, action) => { state['work_exc-dict'] = action.payload; },
    setPeltonWheelDict: (state, action) => { state['pelton_wheel-dict'] = action.payload; },
    setClroDict: (state, action) => { state['clro-dict'] = action.payload; },
    setTurboChargerDict: (state, action) => { state['turbo_charger-dict'] = action.payload; },
    setPressExcDict: (state, action) => { state['press_exc-dict'] = action.payload; },

    // Flow and stream data
    setFlowDict: (state, action) => { state.flow_dict = action.payload; },
    setFlowSheetDict: (state, action) => { state.flow_sheet_dict = action.payload; },
    setFlowSheetDictWastage: (state, action) => { state.flow_sheet_dict_wastage = action.payload; },
    setFlowSheetDictNfWastage: (state, action) => { state.flow_sheet_dict_nfwastage = action.payload; },
    setProductFlowDict: (state, action) => { state.product_flow_dict = action.payload; },
    setMiscFeedFlowDict: (state, action) => { state.feed_flow_dict = action.payload; },
    setStreamNumDict: (state, action) => { state.streamNum_dict = action.payload; },
    updateStreamNumDict: (state, action) => {
      const { key, value } = action.payload;
      if (value === undefined) {
        delete state.streamNum_dict[key];
      } else {
        state.streamNum_dict[key] = value;
      }
    },
    setLocalpath: (state, action) => { state.localpath = action.payload; },

    // Project data
    setProjectInfo: (state, action) => { state.project_info = action.payload; },
    setStoreNodes: (state, action) => { state.storeNodes = action.payload; },
    setForm: (state, action) => { state.form = action.payload; },

    // Output dictionaries
    setCfOutputDict: (state, action) => { state['cf-output-dict'] = action.payload; },
    setCdOutputDict: (state, action) => { state['cd-output-dict'] = action.payload; },
    setEdiOutputDict: (state, action) => { state['edi-output-dict'] = action.payload; },

    // Unit operation names (dynamic keying by ID)
    setUnitOpName: (state, action) => {
      const { id, name } = action.payload;
      state.unitOpNames[id] = name;
    },
    removeUnitOpName: (state, action) => {
      const id = action.payload;
      delete state.unitOpNames[id];
    },
    clearUnitOpNames: (state) => {
      state.unitOpNames = {};
    },

    // Temp and working data
    setCurrentUnitWork: (state, action) => { state['current-unit-work'] = action.payload; },
    setPeltonUnitWork: (state, action) => { state['pelton-unit-work'] = action.payload; },
    setTurboUnitWork: (state, action) => { state['turbo-unit-work'] = action.payload; },

    // Active equation data
    setActiveDualequationCCPP: (state, action) => { state.Active_DualequationCCPP = action.payload; },

    // Tab-specific UI state
    setGlobalExitBtn: (state, action) => { state.globalExitBtn = action.payload; },

    // Remove specific key from mixSplit-dict
    removeMixSplitDictItem: (state, action) => {
      const { item, key } = action.payload;
      if (state['mixSplit-dict'] && state['mixSplit-dict'][item]) {
        delete state['mixSplit-dict'][item][key];
      }
    },

    // Reset to initial state
    resetMiscState: () => initialState
  }
});

export const {
  // Execute/Runtime data (formerly in Context API)
  setExecuteData,
  updateExecuteData,
  setMiscKeyValue,
  setReportData,
  setFlowSheetSolvdData,
  setStreamInputDictContext,
  setRoOuputData,
  clearExecuteRuntimeData,

  // Equipment dictionaries
  setStripperDict,
  setMixSplitDict,
  setCfDict,
  setHpPumpDict,
  setPumpDict,
  setWorkExcDict,
  setPeltonWheelDict,
  setClroDict,
  setTurboChargerDict,
  setPressExcDict,

  // Flow and stream data
  setFlowDict,
  setFlowSheetDict,
  setFlowSheetDictWastage,
  setFlowSheetDictNfWastage,
  setProductFlowDict,
  setMiscFeedFlowDict,
  setStreamNumDict,
  updateStreamNumDict,
  setLocalpath,

  // Project data
  setProjectInfo,
  setStoreNodes,
  setForm,

  // Output dictionaries
  setCfOutputDict,
  setCdOutputDict,
  setEdiOutputDict,

  // Unit operation names
  setUnitOpName,
  removeUnitOpName,
  clearUnitOpNames,

  // Temp and working data
  setCurrentUnitWork,
  setPeltonUnitWork,
  setTurboUnitWork,

  // Active equation data
  setActiveDualequationCCPP,

  // Tab-specific UI state
  setGlobalExitBtn,

  // MixSplit item removal
  removeMixSplitDictItem,

  // Reset
  resetMiscState
} = miscSlice.actions;

// Stable default values to prevent new object creation on each selector call
const DEFAULT_EMPTY_OBJECT = {};
const DEFAULT_EMPTY_STRING = '';

// Selectors for accessing misc state from the active tab
export const selectActiveTabMisc = (state) => {
  const { activeTabId } = state.tabs;
  return state.tabs.tabStates[activeTabId]?.misc || initialState;
};

// Execute/Runtime data selectors (formerly in Context API)
export const selectActiveTabExecuteData = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc.executeData || DEFAULT_EMPTY_OBJECT;
};

export const selectActiveTabReportData = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc.reportData || DEFAULT_EMPTY_OBJECT;
};

export const selectActiveTabFlowSheetSolvdData = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc.flowSheetSolvdData || DEFAULT_EMPTY_OBJECT;
};

export const selectActiveTabStreamInputDict = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc.stream_input_dictContext || DEFAULT_EMPTY_STRING;
};

export const selectActiveTabRoOuputData = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc.ro_ouput_data || DEFAULT_EMPTY_STRING;
};

export const selectActiveTabUnitOpNames = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc.unitOpNames || DEFAULT_EMPTY_OBJECT;
};

export const selectActiveTabMixSplitDict = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc['mixSplit-dict'] || DEFAULT_EMPTY_STRING;
};

export const selectActiveTabFlowSheetDict = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc.flow_sheet_dict || DEFAULT_EMPTY_STRING;
};

export const selectActiveTabStoreNodes = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc.storeNodes || DEFAULT_EMPTY_STRING;
};

export const selectActiveTabProjectInfo = (state) => {
  const misc = selectActiveTabMisc(state);
  return misc.project_info || DEFAULT_EMPTY_STRING;
};

export const selectActiveTabGlobalExitBtn = (state) => {
  const misc = selectActiveTabMisc(state);
  return Boolean(misc.globalExitBtn);
};

export default miscSlice.reducer;
